import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';

import { ThemeProvider, StyledEngineProvider} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import makeStyles from '@mui/styles/makeStyles';
import Colors from '../../ckcore/core/Colors';

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import Config from '../../ckcore/core/Config';
import User from '../../ckcore/core/User';

import {Roles, getRoleName, getRoleStatus } from '../../ckcore/core/Roles';
import {getStatusColor, getTxTimeText} from '../../ckcore/core/TxUtils';
import { getDisplayPhone } from '../../ckcore/core/Telcos';

import AddStaffDialog from './AddStaffDialog';
import EditStaffRoleDialog from './EditStaffRoleDialog';
import RemoveStaffDialog from './RemoveStaffDialog';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import ChangeStaffStatusDialog from './ChangeStaffStatusDialog';
// ... import styles, Colors, etc.

export default function StaffList(props) {
  const classes = useStyle();
  const { keycloak } = useKeycloak();

  const { userObj, partnerObj, refreshCallback } = props;
  const activePartnerId = userObj.partnerId;  
  // Lấy role đang active: (User.getActiveRole() -> staff/manager/admin)
  const activeRole = User.getActiveRole(); // "ADMIN", "MANAGER", "STAFF", or "NONE"

  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showChangeStatusDialog, setShowChangeStatusDialog] = useState(false);

  // Mở dialog change status
  const changeStatusDialog = (staff) => {
    setSelectedStaff(staff);
    setShowChangeStatusDialog(true);
  };
  const closeChangeStatusDialog = () => {
    setShowChangeStatusDialog(false);
    loadStaffList();
  };
  
  // Gọi API Keycloak backend: GET /api/partners/{partnerId}/staff
  const loadStaffList = async () => {
    if (!keycloak?.token) return;
    setLoading(true);
    try {
      const resp = await fetch(Config.getServerStaffURL("staff"), {
        headers: {
          'Authorization': `Bearer ${keycloak.token}`
        }
      });
      const data = await resp.json();
      if (data.staff) {
        setStaffList(data.staff);
      }
    } catch (error) {
      console.error("Error load staff list:", error);
    }
    setLoading(false);
  };

  // Mở dialog thêm
  const openAddStaffDialog = () => {
    setShowAddDialog(true);
  };
  const closeAddStaffDialog = () => {
    setShowAddDialog(false);
    loadStaffList(); // refresh
  };

  // Mở dialog edit
  const editStaffDialog = (staff) => {
    setSelectedStaff(staff);
    setShowEditDialog(true);
  };
  const closeEditStaffDialog = () => {
    setShowEditDialog(false);
    loadStaffList();
  };

  // Mở dialog remove
  const removeStaffDialog = (staff) => {
    setSelectedStaff(staff);
    setShowRemoveDialog(true);
  };
  const closeRemoveStaffDialog = () => {
    setShowRemoveDialog(false);
    loadStaffList();
  };

  // Chỉ admin (hoặc manager) được sửa/xoá
  const canEdit = (staff) => {
    // admin => sửa/xoá tất cả
    if (activeRole === Roles.ADMIN) return true;
    // manager => chỉ sửa staff
    if (activeRole === Roles.MANAGER && staff.role === "staff") {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (User.hasActivePartner(userObj)) {
      loadStaffList();
    }
  }, [userObj.partnerId]); // load lại khi đổi partner

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <div style={{ marginTop: 16 }}>
          <Grid justifyContent="space-between" alignItems="flex-end" container>
            <Grid item className={classes.transHead}>
              <Box display="inline" alignItems="flex-end">
                <span className={classes.title}>DANH SÁCH THÀNH VIÊN của</span>
                <span className={classes.name}>{partnerObj.name}</span>
              </Box>
            </Grid>
            <Grid item>
            </Grid>

            <Grid item>
              <Box display="inline">            
                <Button disabled={(activeRole !== Roles.ADMIN)} color="primary" size="small" className={classes.actionButton} onClick={() => openAddStaffDialog()}>Thêm thành viên</Button>
              </Box>
            </Grid>        
          </Grid>

          <Paper style={{ marginTop: 16 }}>
            <Table size="small">             
              <TableHead>
                <TableRow  className={classes.row}>
                  <TableCell className={classes.colNo}>STT</TableCell>
                  <TableCell className={classes.colStatus}>Email</TableCell>
                  <TableCell className={classes.colPhone}>SĐT</TableCell>
                  <TableCell className={classes.colPhone}>Họ tên</TableCell>
                  <TableCell className={classes.colMessage}>Vai trò</TableCell>
                  <TableCell className={classes.colMessage}>Trạng thái</TableCell>
                  <TableCell className={classes.colExpired}>Cập nhật</TableCell>
                  <TableCell className={classes.colActions}>Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staffList.map((staff, index) => (
                  <TableRow key={index} hover>
                    <TableCell className={classes.colNo}>{index + 1}</TableCell>
                    <TableCell>{staff.email}</TableCell>
                    <TableCell className={(userObj.email === staff.email) ? classes.activeStaff : ""} >{getDisplayPhone(staff.email)}</TableCell>
                    <TableCell>{staff.name}</TableCell>
                    <TableCell className={(userObj.email === staff.email) ? classes.activeStaff : ""} >
                      {
                        ((userObj.email === staff.email) && User.isOwner()) ? getRoleName(Roles.OWNER) : getRoleName(staff.role)
                      }
                    </TableCell>
                    <TableCell className={classes.colMessage} style={{color: getStatusColor(staff.statusId)}}>{getRoleStatus(staff.statusId)}</TableCell>
                    <TableCell className={classes.colMessage} >{getTxTimeText(staff.updated)}</TableCell>

                    <TableCell>
                      {canEdit(staff) && (
                        <>
                          <Tooltip title="Sửa role">
                            <IconButton size="small" onClick={() => editStaffDialog(staff)}>
                              <EditOutlinedIcon/>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Xoá khỏi tổ chức">
                            <IconButton size="small" onClick={() => removeStaffDialog(staff)}>
                              <DeleteOutlinedIcon/>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={staff.enabled ? "Khóa user" : "Mở khóa user"}>
                            <IconButton size="small" onClick={() => changeStatusDialog(staff)}>
                              {staff.enabled ? <LockOutlinedIcon/> : <LockOpenOutlinedIcon/>}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {staffList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Chưa có thành viên nào
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Dialog thêm staff */}
          {showAddDialog && (
            <AddStaffDialog
              open={showAddDialog}
              onClose={closeAddStaffDialog}
              partnerId={activePartnerId}
            />
          )}

          {/* Dialog sửa role */}
          {showEditDialog && selectedStaff && (
            <EditStaffRoleDialog
              open={showEditDialog}
              onClose={closeEditStaffDialog}
              staff={selectedStaff}
              partnerId={activePartnerId}
            />
          )}

          {/* Dialog xoá staff */}
          {showRemoveDialog && selectedStaff && (
            <RemoveStaffDialog
              open={showRemoveDialog}
              onClose={closeRemoveStaffDialog}
              staff={selectedStaff}
              partnerId={activePartnerId}
            />
          )}

          {showChangeStatusDialog && selectedStaff && (
            <ChangeStaffStatusDialog
              open={showChangeStatusDialog}
              onClose={closeChangeStatusDialog}
              staff={selectedStaff}
              partnerId={activePartnerId}
            />
          )}

        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: "2px 6px",
          },
          sizeSmall: {
            padding: "2px 3px 2px 3px",
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            width: 18,
            height: 18,
          },
        },
      },
    },
    palette: {
      primary: {
        main: Colors.BG_BLUE,
      },
      secondary: {
        main: Colors.LIGHT_BLUE,
        contrastText: "#fff",
      },
      action: {},
      text: {},
    },
  });
  
  
  
  const useStyle = makeStyles((theme) => ({
    paper: {
      padding: theme.spacing(2),
      textAlign: "center",
      color: "#37517f",
      fontWeight: 500,
      lineHeight: 1.6,
      letterSpacing: "0.0075em",
    },
    title: {
      color: Colors.DARK_BLUE,
      paddingLeft: 2,
    },
    name: {
      color: Colors.DARK_GREEN,
      fontWeight: 'bold',
      paddingLeft: 8,
    },
    toolbar_height: {
      height: "30px",
    },
    transHead: {
      minHeight: 30,
      padding: "3px 3px",
    },
  
    actionButton: {
      color: Colors.CK_BUTTON,
      fontWeight: 'bold',
      marginLeft: 3,
    },
  
    table: {
      whiteSpace: "nowrap",
      marginTop: 4,
      marginBottom: 0,
    },
  
    row: {
      height: 30,
      maxHeight: 36,
      fontSize: 12,
    },
  
    colNo: {
      minWidth: 30,
      textAlign: "center",
      padding: "2px 3px",
    },
  
    activeStaff: {
      fontWeight: 'bold',
      color: Colors.DARK_BLUE,
    },
  
    
  }));
  